import {useEffect, useRef} from "react";
import type {UseFormReturn} from "react-hook-form";
import type {OfferingFormCheckoutFormValues} from "../../components/offering-form/general/checkout-form/types";

const SYNC_DEBOUNCE_MS = 300;

type UseSyncCheckoutFormToContextArgs<TSaveActionType extends string> = {
  checkoutFormMethods: UseFormReturn<OfferingFormCheckoutFormValues>;
  dispatch: (action: {
    type: TSaveActionType;
    payload: Partial<OfferingFormCheckoutFormValues>;
  }) => void;
  saveActionType: TSaveActionType;
  formData: OfferingFormCheckoutFormValues;
};

export function useSyncCheckoutFormToContext<TSaveActionType extends string>({
  checkoutFormMethods,
  dispatch,
  saveActionType,
  formData,
}: UseSyncCheckoutFormToContextArgs<TSaveActionType>) {
  const hasHydratedRef = useRef(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasHydratedRef.current) {
      return;
    }
    hasHydratedRef.current = true;

    if ((formData.bookingQuestions?.length ?? 0) > 0) {
      checkoutFormMethods.reset(formData);
    }
  }, [checkoutFormMethods, formData]);

  useEffect(() => {
    const subscription = checkoutFormMethods.watch((values) => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      syncTimeoutRef.current = setTimeout(() => {
        dispatch({
          type: saveActionType,
          payload: {
            bookingQuestions: (values.bookingQuestions ??
              []) as OfferingFormCheckoutFormValues["bookingQuestions"],
          },
        });
      }, SYNC_DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [checkoutFormMethods, dispatch, saveActionType]);
}
